import { z } from 'zod';

import { assertNever } from '../../assertNever';
import { ApiKeyId, BorrowerId, UserId } from '../../BrandedIds';
import { Actor } from '../../events/BaseLoanEvent';

/**
 * Export-side translation of the internal {@link Actor} type. Differences from internal:
 *   - `id` is renamed per variant (`userId`, `borrowerId`, `apiKeyId`) so the export is
 *     self-describing to downstream consumers.
 *   - The `'internal'` variant becomes `'willow'` — the internal name is ambiguous to a customer
 *     consuming the export (could read as "internal to my company"); `willow` names the actor
 *     concretely as the Willow servicing platform's own automation.
 *
 * Keep this union in lockstep with `Actor` — `actorToExportableActor` uses `assertNever` so a
 * new variant on `Actor` will fail to compile here until it's added below.
 */
export const ExportableActor = z.discriminatedUnion('type', [
  z.object({ type: z.literal('user'), userId: UserId }),
  z.object({ type: z.literal('borrower'), borrowerId: BorrowerId }),
  z.object({ type: z.literal('willow') }),
  z.object({ type: z.literal('api'), apiKeyId: ApiKeyId }),
]);

export type ExportableActor = z.infer<typeof ExportableActor>;

export const actorToExportableActor = (actor: Actor): ExportableActor => {
  switch (actor.type) {
    case 'user':
      return { type: 'user', userId: actor.id };
    case 'borrower':
      return { type: 'borrower', borrowerId: actor.id };
    case 'internal':
      return { type: 'willow' };
    case 'api':
      return { type: 'api', apiKeyId: actor.id };
    default:
      return assertNever(actor);
  }
};
